import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Save, X, Upload, Download, ArrowLeft, Crown } from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton, BrutalCard, BrutalInput, BrutalLabel, Badge, SectionTitle,
} from "../../components/common/Brutal";

const empty = { code: "", label: "", area: "", active: true };

export default function AdminVVIPCodes() {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null); // id | "new" | null
  const [form, setForm] = useState(empty);
  const fileRef = useRef(null);

  const load = () => api.get("/vvip-qr-codes").then(({ data }) => setCodes(data));

  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(empty); };
  const startEdit = (c) => { setEditing(c.id); setForm({ ...c }); };
  const cancel = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    if (!form.code) return toast.error("Code is required");
    try {
      if (editing === "new") await api.post("/vvip-qr-codes", form);
      else await api.put(`/vvip-qr-codes/${editing}`, form);
      toast.success("Saved");
      cancel();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this VVIP QR code?")) return;
    await api.delete(`/vvip-qr-codes/${id}`);
    load();
  };

  const exportCSV = () => {
    const header = "code,label,area,active";
    const rows = codes.map((c) =>
      [c.code, c.label || "", c.area || "", c.active ? "true" : "false"]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `vvip-qr-codes-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return [];
    const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ""));
    const ix = (k) => header.indexOf(k);
    return lines.slice(1).map((line) => {
      // basic CSV parse honouring quotes
      const cells = [];
      let cur = "", inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; }
        else if (ch === '"') inQ = !inQ;
        else if (ch === "," && !inQ) { cells.push(cur); cur = ""; }
        else cur += ch;
      }
      cells.push(cur);
      return {
        code: cells[ix("code")] || "",
        label: ix("label") >= 0 ? cells[ix("label")] : "",
        area: ix("area") >= 0 ? cells[ix("area")] : "",
      };
    }).filter((r) => r.code);
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = parseCSV(text);
    if (rows.length === 0) {
      toast.error("No rows found. CSV needs a 'code' column.");
      e.target.value = "";
      return;
    }
    try {
      const { data } = await api.post("/vvip-qr-codes/bulk", { codes: rows });
      toast.success(`Imported: ${data.created} created · ${data.skipped} skipped`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-vvip-codes">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <SectionTitle kicker="VVIP Access Control" title="VVIP QR Codes" icon={Crown} />
        <Link to="/admin/vvip-tracker" className="text-xs font-bold uppercase tracking-wider text-[#3D1E6D] hover:text-[#E5247C] inline-flex items-center gap-1.5">
          <ArrowLeft className="h-3 w-3" /> Back to Tracker
        </Link>
      </div>

      <BrutalCard className="p-4 flex flex-wrap items-center gap-3">
        <p className="text-sm text-[#6B4E9E] flex-1 min-w-[200px]">
          Manage the list of registered VVIP passes. Only codes present here will grant access at gates.
        </p>
        <input ref={fileRef} type="file" accept=".csv" onChange={onImportFile} className="hidden" data-testid="vvip-import-file" />
        <BrutalButton variant="outline" onClick={() => fileRef.current?.click()} data-testid="vvip-import"><Upload className="h-4 w-4" /> Import CSV</BrutalButton>
        <BrutalButton variant="outline" onClick={exportCSV} data-testid="vvip-export-csv"><Download className="h-4 w-4" /> Export CSV</BrutalButton>
        {!editing && <BrutalButton variant="primary" onClick={startNew} data-testid="vvip-add"><Plus className="h-4 w-4" /> Add Code</BrutalButton>}
      </BrutalCard>

      {editing && (
        <BrutalCard className="p-5" data-testid="vvip-form">
          <h3 className="display text-2xl mb-3">{editing === "new" ? "Add VVIP Code" : "Edit VVIP Code"}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="QR Code *"><BrutalInput value={form.code} onChange={(e) => setForm({...form, code: e.target.value})} data-testid="vvip-form-code" placeholder="e.g. VVIP-001" /></Field>
            <Field label="Label"><BrutalInput value={form.label} onChange={(e) => setForm({...form, label: e.target.value})} data-testid="vvip-form-label" placeholder="e.g. Backstage Pass" /></Field>
            <Field label="Area"><BrutalInput value={form.area} onChange={(e) => setForm({...form, area: e.target.value})} data-testid="vvip-form-area" placeholder="e.g. Main Stage Backstage" /></Field>
            <Field label="Active">
              <select className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5" value={form.active ? "true" : "false"} onChange={(e) => setForm({...form, active: e.target.value === "true"})} data-testid="vvip-form-active">
                <option value="true">Active</option>
                <option value="false">Disabled</option>
              </select>
            </Field>
          </div>
          <div className="mt-4 flex gap-2">
            <BrutalButton variant="primary" onClick={save} data-testid="vvip-save"><Save className="h-4 w-4" /> Save</BrutalButton>
            <BrutalButton variant="outline" onClick={cancel}><X className="h-4 w-4" /> Cancel</BrutalButton>
          </div>
        </BrutalCard>
      )}

      <BrutalCard className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="vvip-codes-table">
          <thead className="bg-[#FFB400]">
            <tr>
              {["Code","Label","Area","Status","Created","Action"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] font-bold uppercase tracking-widest border-b-2 border-[#3D1E6D]">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {codes.map((c, i) => (
              <tr key={c.id} className={i % 2 ? "bg-[#FFF4E6]" : "bg-white"} data-testid={`vvip-row-${c.id}`}>
                <td className="px-3 py-2 font-bold">{c.code}</td>
                <td className="px-3 py-2 text-[#6B4E9E]">{c.label}</td>
                <td className="px-3 py-2 text-[#6B4E9E]">{c.area}</td>
                <td className="px-3 py-2">{c.active ? <Badge color="green">ACTIVE</Badge> : <Badge color="red">DISABLED</Badge>}</td>
                <td className="px-3 py-2 text-xs">{c.created_at && new Date(c.created_at).toLocaleDateString()}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button onClick={() => startEdit(c)} className="kf-border bg-white p-1.5" data-testid={`vvip-edit-${c.id}`}><Pencil className="h-3 w-3" /></button>
                    <button onClick={() => remove(c.id)} className="kf-border bg-[#EF4444] text-white p-1.5" data-testid={`vvip-delete-${c.id}`}><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {codes.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-[#6B4E9E]">No VVIP codes yet. Add manually or import via CSV.</td></tr>}
          </tbody>
        </table>
      </BrutalCard>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);
